import { useCallback, useMemo, useState, useEffect } from 'react';

/**
 * Enhanced Block Events Hook (Simplified)
 * Sends basic actions to target elements. Target elements handle their own configuration.
 */
export const useEnhancedBlockEvents = (blockSettings, defaultEventLabel = '', blockType = 'block') => {
  // Extract action settings
  const enableActions = blockSettings.enable_actions;
  const actionTrigger = blockSettings.action_trigger || 'click';
  const actionPreset = blockSettings.action_preset;
  const targetEventId = blockSettings.target_event_id;
  const customActions = blockSettings.custom_actions;
  const actionDelay = blockSettings.action_delay || 0;

  // Event ID configuration (simplified)
  const customEventId = blockSettings.event_id;

  // Loading state management
  const [isLoading, setIsLoading] = useState(false);

  // Listen for cart loading end events
  useEffect(() => {
    const handleLoadingEnd = () => {
      setIsLoading(false);
    };

    window.addEventListener('cart:add_loading_end', handleLoadingEnd);
    window.addEventListener('cart:add_error', handleLoadingEnd); // Also clear on error

    return () => {
      window.removeEventListener('cart:add_loading_end', handleLoadingEnd);
      window.removeEventListener('cart:add_error', handleLoadingEnd);
    };
  }, []);

  // Generate unique event ID based on block settings
  const eventId = useMemo(() => {
    if (blockSettings.event_id) {
      return blockSettings.event_id;
    }

    // Generate unique event ID from block info
    const blockId = blockSettings.block_id || blockSettings.id;
    return `${blockType}-${blockId}-action`;
  }, [blockSettings, blockType]);

  // Simple action data (basic information only)
  const actionData = useMemo(() => {
    return {
      eventId: eventId,
      blockId: blockSettings.block_id || blockSettings.id,
      blockType: blockType,
      sectionId: blockSettings.section_id,
      customEventId: customEventId
    };
  }, [blockSettings, blockType, eventId, customEventId]);

  // Parse actions based on mode (preset or multiple)
  const actionConfig = useMemo(() => {
    if (!enableActions) return [];

    // Action name mapping to existing actions in frontend/src/actions/
    const actionMapping = {
      // Cart actions
      add_to_cart: 'runAddToCart',
      add_to_cart_flyout: 'runAddToCartFlyout',
      add_to_cart_open_cart: 'runAddToCartAndCartOpen',
      quick_add_to_cart: 'runQuickAddToCart',
      open_cart: 'runCartOpen',
      close_cart: 'runCartClose',
      toggle_cart: 'runCartToggle',
      remove_cart_item: 'runRemoveCartItem',
      update_cart_quantity: 'runUpdateCartQuantity',
      basket_api_sync: 'runBasketApiSync',

      // Drawer/Toggle actions
      open_drawer: 'runDrawerOpen',
      close_drawer: 'runDrawerClose',
      toggle_drawer: 'runDrawerToggle',
      open_popup: 'runPopupOpen',
      close_popup: 'runPopupClose',
      toggle_popup: 'runPopupToggle',
      show_element: 'runElementShow',
      hide_element: 'runElementHide',
      toggle_visibility: 'runVisibilityToggle',
      show_backdrop: 'runBackdropShow',
      hide_backdrop: 'runBackdropHide',
      toggle_backdrop: 'runBackdropToggle',

      // Search actions
      open_search: 'runSearchOpen',
      close_search: 'runSearchClose',
      search_action: 'runSearchAction',
      search_clear: 'runSearchClear',

      // Quantity actions
      quantity_increase: 'runQuantityIncrease',
      quantity_decrease: 'runQuantityDecrease',
      quantity_set: 'runQuantitySet',
      manage_quantity: 'runManageQuantity',

      // Wishlist actions
      wishlist_add: 'runWishlistAdd',
      wishlist_remove: 'runWishlistRemove',
      wishlist_get: 'runWishlistGet',
      add_to_wishlist: 'runAddToWishlistWithContext',

      // Product/Variant actions
      select_variant: 'runSelectVariant',
      select_selling_plan: 'runSelectSellingPlan',
      initialize_variants: 'runInitializeVariants',
      model_view_toggle: 'runModelViewToggle',

      // Customer actions
      customer_login: 'runCustomerLogin',
      customer_register: 'runCustomerRegister',
      customer_forgot_password: 'runCustomerForgotPassword',
      customer_subscribe: 'runCustomerSubscribe',
      customer_address_update: 'runCustomerAddressUpdate',
      customer_address_delete: 'runCustomerAddressDelete',
      customer_details_update: 'runCustomerDetailsUpdate',
      customer_rebuy_order: 'runCustomerRebuyOrder',

      // Utility actions
      show_notification: 'runShowNotification',
      background_changer: 'runBackgroundChanger',
      manage_add_button: 'runManageAddButton',
      stock_lookup: 'runStockLookup',
      find_in_store: 'runFindInStore',
      order_lookup: 'runOrderLookup',
      out_of_stock_subscribe: 'runOutOfStockNotificationSubscribe',
      scroll_to_top: 'runScrollToTop',

      // Currency actions
      get_currency: 'runGetCurrency',
      set_currency: 'runSetCurrency',

      // Recently viewed actions
      recently_viewed_add: 'runRecentlyViewedAdd',
      recently_viewed_get: 'runRecentlyViewedGet',
      recently_viewed_manage: 'runRecentlyViewedManage',

      // Discount actions
      apply_discount_code: 'runApplyDiscountCode',
      apply_gift_card: 'runApplyGiftCard'
    };

    if (actionPreset !== 'custom') {
      const mappedAction = actionMapping[actionPreset] || actionPreset;

      return [
        {
          action: mappedAction,
          eventId: ''
        }
      ];
    } else {
      // Custom event IDs
      if (!customActions || customActions.trim() === '') return [];

      // Split by commas and process each event ID
      const eventIds = customActions.split(',');

      const parsedActions = eventIds
        .map((eventId) => eventId.trim())
        .filter(Boolean)
        .map((eventId) => {
          const mappedAction = actionMapping[eventId] || '';

          return {
            action: mappedAction ? mappedAction : 'custom_event',
            eventId: mappedAction ? '' : eventId
          };
        });

      return parsedActions;
    }
  }, [enableActions, actionPreset, customEventId, customActions]);

  // Function to trigger actions
  const triggerActions = useCallback(
    async (triggerType = 'click', customData = {}) => {
      // Only trigger if actions are enabled and trigger type matches
      if (!enableActions || actionTrigger !== triggerType || actionConfig.length === 0) {
        return;
      }

      const baseData = {
        ...actionData,
        ...customData,
        triggerType,
        timestamp: Date.now()
      };

      // Check if any of the actions are cart-related
      const hasCartActions = actionConfig.some(
        ({ action }) =>
          action &&
          (action === 'runAddToCart' || action === 'runAddToCartAndCartOpen' || action === 'runQuickAddToCart')
      );

      try {
        // Set loading state if cart actions are involved
        if (hasCartActions) {
          setIsLoading(true);
        }

        // Execute actions sequentially with delay if specified
        for (let i = 0; i < actionConfig.length; i++) {
          const { action, eventId: targetEventId } = actionConfig[i];

          // Add delay between actions (except for the first one)
          if (i > 0 && actionDelay > 0) {
            await new Promise((resolve) => setTimeout(resolve, actionDelay));
          }

          const finalData = {
            ...baseData,
            eventId: targetEventId,
            targetEventId,
            actionIndex: i
          };

          if (action === 'custom_event' && targetEventId) {
            // Emit custom event for interaction control
            window.dispatchEvent(new CustomEvent(targetEventId, { detail: finalData }));
          } else if (action !== 'custom_event') {
            // Use existing action system for global actions (add_to_cart, etc.)
            window.triggerAction(action, finalData);
          }
        }
      } catch (error) {
        console.error('Failed to trigger actions:', error);
        // Clear loading state on error (since no loading end event will be fired)
        if (hasCartActions) {
          setIsLoading(false);
        }
      }
    },
    [enableActions, actionTrigger, actionConfig, actionData, actionDelay]
  );

  // Event handlers for different trigger types (with debouncing)
  const handleClick = useCallback(
    (customData = {}) => {
      const now = Date.now();
      if (handleClick._lastCall && now - handleClick._lastCall < 100) {
        return;
      }
      handleClick._lastCall = now;

      triggerActions('click', customData);
    },
    [triggerActions]
  );

  const handleMouseEnter = useCallback(
    (customData = {}) => {
      const now = Date.now();
      if (handleMouseEnter._lastCall && now - handleMouseEnter._lastCall < 50) {
        return;
      }
      handleMouseEnter._lastCall = now;

      triggerActions('hover', customData);
    },
    [triggerActions]
  );

  const handleMouseLeave = useCallback(
    (customData = {}) => {
      triggerActions('hover_end', customData);
    },
    [triggerActions]
  );

  const handleFocus = useCallback(
    (customData = {}) => {
      triggerActions('focus', customData);
    },
    [triggerActions]
  );

  const handleBlur = useCallback(
    (customData = {}) => {
      triggerActions('blur', customData);
    },
    [triggerActions]
  );

  const handleChange = useCallback(
    (value, customData = {}) => {
      triggerActions('change', { value, ...customData });
    },
    [triggerActions]
  );

  const handleSubmit = useCallback(
    (customData = {}) => {
      triggerActions('submit', customData);
    },
    [triggerActions]
  );

  return {
    // Settings
    enableActions,
    actionConfig,
    actionTrigger,
    eventId,

    // Loading state
    isLoading,

    // Core functions
    triggerActions,

    // Event handlers
    handleClick,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
    handleChange,
    handleSubmit,

    // Action data
    actionData
  };
};
